import {
  Block,
  DefaultBlockSchema,
  PartialBlock,
} from '@packages/blocknote-core'
import classNames from 'classnames'
import styles from '@packages/blocknote-core/extensions/Blocks/nodes/Block.module.css'
import { useTaskToggle } from '../../../hooks/useTaskToggle'
import {
  isBlockType,
  TASK_BLOCK_TYPES,
  getBlockDataAttributes,
  isBlockChecked,
  type ItemMetadata,
} from './types'
import { BlockContent } from './BlockContent'
import { getSourceNoteTitle, formatScheduledDate } from './noteUtils'
import { Note } from '../../../utils/note'

type BlockRendererProps = {
  block: Block<DefaultBlockSchema> | PartialBlock<DefaultBlockSchema>
  metadata: ItemMetadata
  onNavigate?: () => void
  className?: string
  showTaskToggle?: boolean
  showSourceNote?: boolean
  showScheduledDate?: boolean
}

export function BlockRenderer({
  block,
  metadata,
  onNavigate,
  className,
  showTaskToggle = true,
  showSourceNote = false,
  showScheduledDate = false,
}: BlockRendererProps) {
  // Check if this is a task or check list item
  const isTaskBlock = isBlockType(block, TASK_BLOCK_TYPES)

  // Get data attributes for CSS styling with context-aware scheduling
  const dataAttributes = getBlockDataAttributes(block, {
    scheduledDate: metadata.scheduledDate,
    context: metadata.context,
  })

  // Get checked state efficiently for checkbox input
  const isChecked = isBlockChecked(block)

  // Handle task toggling internally
  const taskToggle = useTaskToggle({
    recordName: metadata.recordName,
    noteType: metadata.noteType,
    lineIndex: metadata.lineIndex ?? 0,
    expectedContent: metadata.expectedContent,
  })

  return (
    <div className={`block-renderer ${className ?? ''}`}>
      <div
        className={`${styles.blockGroup} cursor-pointer`}
        data-node-type='blockGroup'
        onClick={onNavigate}
      >
        <div
          data-id={block.id}
          className={styles.blockOuter}
          data-node-type='block-outer'
        >
          <div className={styles.block} data-node-type='blockContainer'>
            <div className='flex items-start justify-between'>
              <div className='flex min-w-0 flex-1 items-start'>
                <div
                  className={classNames({
                    pulse: taskToggle.isProcessing,
                    [styles.blockContent]: true,
                    'font-bold text-zinc-500 dark:text-zinc-400':
                      block.type === 'heading',
                    'text-[15px] dark:opacity-90': block.type !== 'heading',
                    relative: isTaskBlock && showTaskToggle,
                    'flex items-start': true,
                    '-ml-3':
                      (block.children?.length ?? 0) > 0 &&
                      block.type !== 'heading',
                    '-ml-2':
                      (block.children?.length ?? 0) > 0 &&
                      block.type === 'heading',
                  })}
                  data-content-type={block.type}
                  {...dataAttributes}
                >
                  {isTaskBlock && showTaskToggle && (
                    <label
                      className={classNames({
                        'pulse opacity-50': taskToggle.isProcessing,
                        'transition-transform hover:scale-110':
                          !taskToggle.isProcessing,
                        'mr-2 cursor-pointer': true,
                      })}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        taskToggle.handleTaskClick(event)
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={isChecked}
                        readOnly
                        disabled={taskToggle.isProcessing}
                      />
                    </label>
                  )}

                  {block.content && (
                    <div className='wrap-anywhere min-w-0 flex-1'>
                      <BlockContent
                        content={block.content}
                        keyword={metadata.keyword}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Right side info */}
              {(showSourceNote || showScheduledDate) && (
                <div className='ml-2 flex flex-col items-end text-right'>
                  {/* Source note (if different) - takes priority over scheduled date */}
                  {showSourceNote && metadata.sourceNote ? (
                    <span className='whitespace-nowrap text-xs italic text-gray-400'>
                      from {getSourceNoteTitle(metadata.sourceNote as Note)}
                    </span>
                  ) : (
                    /* Scheduled date (if scheduled and no source note shown) */
                    showScheduledDate &&
                    metadata.scheduledDate && (
                      <span className='whitespace-nowrap text-xs text-gray-500'>
                        {formatScheduledDate(metadata.scheduledDate)}
                      </span>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {taskToggle.saveError && (
        <div className='ml-6 mt-1 text-xs text-red-500'>
          Error saving: {taskToggle.saveError.message}
        </div>
      )}
    </div>
  )
}
